import { CREW_NOTE_KINDS, type CrewNoteChange, type CrewNoteKind } from '@felix/domain';
import { formatDay } from '../../format.js';
import type { CrewAssignment } from './types.js';

/**
 * Crew Profile ▸ Office notes'un saf modeli (ADR 0159; sahip 2026-11-04 B4). Doğrulama sunucuda
 * (`@felix/domain` `parseCrewNote`); burada gönderme koşulu, gövde ve süzme.
 */

export type { CrewNoteChange, CrewNoteKind };
export { CREW_NOTE_KINDS };

export interface CrewNoteVersion {
  id: string;
  versionNo: number;
  change: CrewNoteChange;
  kind: CrewNoteKind;
  body: string;
  vesselId: string | null;
  vesselName: string | null;
  assignmentId: string | null;
  assignmentFrom: string | null;
  assignmentTo: string | null;
  reason: string | null;
  createdAt: string;
  createdBy: string;
  createdByName: string | null;
}

export interface CrewNote {
  id: string;
  userId: string;
  createdAt: string;
  createdBy: string;
  createdByName: string | null;
  current: CrewNoteVersion;
  withdrawn: boolean;
  /** Yeniden eskiye. */
  versions: CrewNoteVersion[];
}

export interface CrewNoteList {
  notes: CrewNote[];
  canCreate: boolean;
  canCorrect: boolean;
  canWithdraw: boolean;
  canExport: boolean;
}

export interface CrewNoteBody {
  kind: CrewNoteKind;
  body: string;
  vesselId: string | null;
  assignmentId: string | null;
}

/**
 * Ofis notlarının veri kaynağı (yalnız web ofis; ADR 0159). Gemi uygulamasının kaynağında yoktur:
 * notlar gemiye inmez.
 */
export interface CrewNotesSource {
  list(userId: string): Promise<CrewNoteList>;
  create(userId: string, body: CrewNoteBody): Promise<CrewNote>;
  correct(
    noteId: string,
    expectedVersion: number,
    body: CrewNoteBody & { reason: string | null },
  ): Promise<CrewNote>;
  withdraw(noteId: string, expectedVersion: number, reason: string): Promise<CrewNote>;
}

export type CrewNoteFilter = 'all' | CrewNoteKind;

export function filterNotes(notes: readonly CrewNote[], filter: CrewNoteFilter): CrewNote[] {
  return filter === 'all' ? [...notes] : notes.filter((note) => note.current.kind === filter);
}

export interface NoteForm {
  kind: CrewNoteKind;
  body: string;
  /** Boş: yok. */
  vesselId: string;
  assignmentId: string;
  reason: string;
}

export function emptyNoteForm(): NoteForm {
  return { kind: 'note', body: '', vesselId: '', assignmentId: '', reason: '' };
}

/** Düzeltme formu notun güncel sürümünden dolar. */
export function noteFormFrom(note: CrewNote): NoteForm {
  return {
    kind: note.current.kind,
    body: note.current.body,
    vesselId: note.current.vesselId ?? '',
    assignmentId: note.current.assignmentId ?? '',
    reason: '',
  };
}

/** Gönderilecek gövde; metin boşsa `null` (gönderilemez). */
export function noteFormBody(form: NoteForm): CrewNoteBody | null {
  const body = form.body.trim();
  if (body === '') return null;
  return {
    kind: form.kind,
    body,
    vesselId: form.vesselId === '' ? null : form.vesselId,
    assignmentId: form.assignmentId === '' ? null : form.assignmentId,
  };
}

/** Kontrat dönemi seçenekleri: "Gemi · rütbe · başlangıç – bitiş". */
export function contractOptions(
  assignments: readonly CrewAssignment[],
  open: string,
): { value: string; label: string; vesselId: string }[] {
  return assignments.map((row) => ({
    value: row.id,
    label: [
      row.vesselName,
      row.rank,
      `${formatDay(row.validFrom)} – ${row.validTo ? formatDay(row.validTo) : open}`,
    ]
      .filter(Boolean)
      .join(' · '),
    vesselId: row.vesselId,
  }));
}
